import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { isSufeExamClient } from '@/utils/examClient';
import { getExamInfo } from '@/api/start';
import type { IGetExamInfo } from '@/api/start';
import i18n from '@/locales/init';
import PendingWritingSubmission from './PendingWritingSubmission';

type MockCourse = 'listening' | 'reading' | 'writing';
type GuardState = 'checking' | 'retrying' | 'allowed' | 'pending';
interface GuardResult {
  state: GuardState;
  recordId: string | undefined;
  course: MockCourse;
}

const GUARD_RETRY_DELAYS_MS = [3_000, 6_000, 12_000, 24_000, 30_000];

const isDoing = (status?: number) => status === 1 || status === 2;
const isCompleted = (status?: number) => status === 4;

const canOpenCourse = (exam: IGetExamInfo, course: MockCourse) => {
  if (exam.type !== 1) return false;
  if (course === 'listening') return isDoing(exam.listening.status);
  if (course === 'reading') return isCompleted(exam.listening.status) && isDoing(exam.reading.status);
  return isCompleted(exam.listening.status) && isCompleted(exam.reading.status) && isDoing(exam.writing.status);
};

const isPendingWritingSubmission = (exam: IGetExamInfo) => (
  exam.type === 1
  && isCompleted(exam.listening.status)
  && isCompleted(exam.reading.status)
  && exam.writing.status === 3
  && exam.writing.submission_pending === true
);

const hasUnknownWritingSubmissionState = (exam: IGetExamInfo, course: MockCourse) => (
  course === 'writing'
  && exam.writing.status === 3
  && exam.writing.submission_pending === undefined
);

export default function ClientExamGuard({course, children}: PropsWithChildren<{course: MockCourse;}>) {
  const {recordId} = useParams();
  const navigate = useNavigate();
  const restricted = isSufeExamClient();
  const [result, setResult] = useState<GuardResult>({state: 'checking', recordId: undefined, course});
  const generationRef = useRef(0);

  useEffect(() => {
    if (!restricted) return;

    const recordNumber = Number(recordId);
    const generation = ++generationRef.current;
    let attempts = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let request: AbortController | undefined;
    let active = true;
    let settled = false;

    const isCurrent = () => active && !settled && generationRef.current === generation;
    const setCurrentResult = (state: GuardState) => setResult({state, recordId, course});
    const clearRetry = () => {
      clearTimeout(retryTimer);
      retryTimer = undefined;
    };
    const retry = () => {
      if (!isCurrent()) return;
      setCurrentResult('retrying');
      clearRetry();
      const delay = GUARD_RETRY_DELAYS_MS[Math.min(attempts - 1, GUARD_RETRY_DELAYS_MS.length - 1)];
      retryTimer = setTimeout(check, delay);
    };
    const check = () => {
      if (!isCurrent()) return;
      clearRetry();
      request?.abort();
      const currentRequest = new AbortController();
      request = currentRequest;
      attempts += 1;
      setCurrentResult('checking');

      void getExamInfo({record_id: recordNumber}, {signal: currentRequest.signal}).then(response => {
        if (!isCurrent() || currentRequest.signal.aborted) return;
        const exam = response.data;
        if (response.code !== 200 || exam?.record_id !== recordNumber) {
          retry();
          return;
        }
        if (course === 'writing' && isPendingWritingSubmission(exam)) {
          settled = true;
          clearRetry();
          setCurrentResult('pending');
          return;
        }
        if (hasUnknownWritingSubmissionState(exam, course)) {
          retry();
          return;
        }
        if (canOpenCourse(exam, course)) {
          settled = true;
          clearRetry();
          setCurrentResult('allowed');
          return;
        }
        navigate(`/mockstart/${recordId}`, {replace: true});
      }).catch(() => {
        if (!isCurrent() || currentRequest.signal.aborted) return;
        retry();
      });
    };
    const onOnline = () => {
      if (!isCurrent()) return;
      attempts = 0;
      check();
    };

    check();
    window.addEventListener('online', onOnline);
    return () => {
      active = false;
      clearRetry();
      request?.abort();
      window.removeEventListener('online', onOnline);
    };
  }, [course, navigate, recordId, restricted]);

  const isCurrentResult = result.recordId === recordId && result.course === course;
  if (!restricted || (isCurrentResult && result.state === 'allowed')) return <>{children}</>;
  if (isCurrentResult && result.state === 'pending' && recordId) return <PendingWritingSubmission recordId={Number(recordId)} />;

  const state = isCurrentResult ? result.state : 'checking';
  const message = state === 'checking'
    ? i18n.t('common.dauyan_exam_guard_checking')
    : i18n.t('common.dauyan_exam_guard_retrying');
  return <div role="status" aria-live="assertive" aria-busy="true">{message}</div>;
}
